import React from 'react';
import {
  arrayOf,
  bool, func, number, string, exact, objectOf, oneOfType, oneOf,
} from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  Button, Flex, Typography, Modal, Dropdown,
} from '@atoms';
import { DeleteModal, DiscountSection, SpecialWarningModal } from '@molecules';
import { AvailabilityProcessModal, DraggableItems } from '@organisms';

const SpecialProcessModal = ({
  onEditConfirm,
  onSpecialWarningModalClose,
  isSpecialWarningModalOpen,
  availabilities,
  onAvailabilityDetach,
  onAvailabilityEdit,
  onAvailabilityAdd,
  onStartDateRemove,
  onEndDateRemove,
  onStartTimeRemove,
  onEndTimeRemove,
  isAvailabilityConfirmButtonDisabled,
  onAvailabilityDeleteModalChangeState,
  startTime,
  endTime,
  startDate,
  endDate,
  onStartTimeChange,
  onEndTimeChange,
  onStartDateChange,
  onEndDateChange,
  onAvailabilityNameChange,
  onAvailabilityEditSubmit,
  availabilityName,
  isAvailabilityModalOpen,
  onAvailabilityModalChangeState,
  isAvailabilityDeleteModalOpen,
  onAvailabilityDelete,
  onDayClick,
  selectedWeekDays,
  isConfirmButtonDisabled,
  onSpecialEditSubmit,
  discountTypes,
  selectedDiscountType,
  onDiscountTypeChange,
  isModalOpen,
  onModalClose,
  entities,
  selectedEntity,
  onSpecialDelete,
  isSpecialDeleteModalOpen,
  onSpecialDeleteModalChangeState,
  isEditing,
  onSelectedEntityChange,
}) => {
  const specialName = entities.find(entity => entity.id === selectedEntity)?.name;

  return (
    <Modal
      onTop
      isOpen={isModalOpen}
      ariaHideApp={false}
      onRequestClose={onModalClose}
      shouldCloseOnOverlayClick
    >
      {isEditing
        ? <Typography weight="bold" size="xl2">{specialName}</Typography>
        : (
          <Dropdown
            onSelectedOptionChange={onSelectedEntityChange}
            selectedOption={selectedEntity}
            options={entities}
          />
        )}
      <DiscountSection
        discountTypes={discountTypes}
        selectedDiscountType={selectedDiscountType}
        onDiscountTypeChange={onDiscountTypeChange}
      />
      {isEditing && (
        <>
          <Flex column itemsCenter>
            <Typography marginBottom="md" marginTop="xl5" size="xl3" weight="black">
              Availability periods of special
            </Typography>
            <DragDropContext>
              <Droppable droppableId="specialCategoryAvailabilities">
                {provided => (
                  <Flex column ref={provided.innerRef} {...provided.droppableProps}>
                    <DraggableItems
                      onItemClick={onAvailabilityEdit}
                      onItemRemove={onAvailabilityDetach}
                      onItemAdd={onAvailabilityAdd}
                      isDraggable={false}
                      canBeAdded={isEditing}
                      items={availabilities}
                    />
                    {provided.placeholder}
                  </Flex>
                )}
              </Droppable>
            </DragDropContext>
          </Flex>
          <Button.Rectangular
            marginTop="xl"
            onClick={onSpecialDeleteModalChangeState}
            primary
            padding="lg"
          >
            Delete
          </Button.Rectangular>
        </>
      )}
      <Flex>
        <Button.Rectangular
          fullWidth
          primaryInverted
          padding="md"
          onClick={onModalClose}
          marginTop="xl5"
        >
          <Typography size="lg">
            Cancel
          </Typography>
        </Button.Rectangular>
        <Button.Rectangular
          fullWidth
          onClick={onSpecialEditSubmit}
          primary
          padding="md"
          marginLeft="xl"
          marginTop="xl5"
          disabled={isConfirmButtonDisabled}
        >
          <Typography size="lg">
            Save
          </Typography>
        </Button.Rectangular>
      </Flex>
      <DeleteModal
        name={`${specialName} special`}
        isModalOpen={isSpecialDeleteModalOpen}
        onModalClose={onSpecialDeleteModalChangeState}
        onDelete={onSpecialDelete}
      />
      <AvailabilityProcessModal
        onStartDateRemove={onStartDateRemove}
        onEndDateRemove={onEndDateRemove}
        onStartTimeRemove={onStartTimeRemove}
        onEndTimeRemove={onEndTimeRemove}
        isConfirmButtonDisabled={isAvailabilityConfirmButtonDisabled}
        onAvailabilityDeleteModalChangeState={onAvailabilityDeleteModalChangeState}
        startTime={startTime}
        endTime={endTime}
        startDate={startDate}
        endDate={endDate}
        onStartTimeChange={onStartTimeChange}
        onEndTimeChange={onEndTimeChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onAvailabilityNameChange={onAvailabilityNameChange}
        isEditing
        onAvailabilityEditSubmit={onAvailabilityEditSubmit}
        availabilityName={availabilityName}
        isModalOpen={isAvailabilityModalOpen}
        onModalClose={onAvailabilityModalChangeState}
        isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
        onAvailabilityDelete={onAvailabilityDelete}
        onDayClick={onDayClick}
        selectedWeekDays={selectedWeekDays}
      />
      <SpecialWarningModal
        onSave={onEditConfirm}
        onWarningClose={onSpecialWarningModalClose}
        isWarningOpen={isSpecialWarningModalOpen}
      />
    </Modal>
  );
};

SpecialProcessModal.defaultProps = {
  selectedEntity: null,
  onSelectedEntityChange: () => {},
};

SpecialProcessModal.propTypes = {
  onEditConfirm: func.isRequired,
  onSpecialWarningModalClose: func.isRequired,
  isSpecialWarningModalOpen: bool.isRequired,
  availabilities: arrayOf(exact({
    name: string,
    id: number,
  })).isRequired,
  onAvailabilityDetach: func.isRequired,
  onAvailabilityEdit: func.isRequired,
  onAvailabilityAdd: func.isRequired,
  onStartDateRemove: func.isRequired,
  onEndDateRemove: func.isRequired,
  onStartTimeRemove: func.isRequired,
  onEndTimeRemove: func.isRequired,
  isAvailabilityConfirmButtonDisabled: bool.isRequired,
  onAvailabilityModalChangeState: func.isRequired,
  isAvailabilityModalOpen: bool.isRequired,
  onAvailabilityDelete: func.isRequired,
  onAvailabilityDeleteModalChangeState: func.isRequired,
  onAvailabilityEditSubmit: func.isRequired,
  isAvailabilityDeleteModalOpen: bool.isRequired,
  startTime: string.isRequired,
  endTime: string.isRequired,
  startDate: string.isRequired,
  endDate: string.isRequired,
  onStartTimeChange: func.isRequired,
  onEndTimeChange: func.isRequired,
  onStartDateChange: func.isRequired,
  onEndDateChange: func.isRequired,
  onDayClick: func.isRequired,
  selectedWeekDays: arrayOf(number).isRequired,
  onAvailabilityNameChange: func.isRequired,
  availabilityName: string.isRequired,
  isConfirmButtonDisabled: bool.isRequired,
  onSpecialEditSubmit: func.isRequired,
  onSelectedEntityChange: func,
  discountTypes: objectOf(
    exact({
      name: string,
      inputValue: string,
      inputHandler: func,
    }),
  ).isRequired,
  selectedDiscountType: string.isRequired,
  onDiscountTypeChange: func.isRequired,
  selectedEntity: oneOfType([oneOf([null]), number]),
  entities: arrayOf(exact({
    id: number,
    name: string,
    price: number,
  })).isRequired,
  onSpecialDelete: func.isRequired,
  isSpecialDeleteModalOpen: bool.isRequired,
  onSpecialDeleteModalChangeState: func.isRequired,
  isEditing: bool.isRequired,
  onModalClose: func.isRequired,
  isModalOpen: bool.isRequired,
};

export default SpecialProcessModal;
